import { Modal, Stack } from "@mantine/core"
import { IconAlertCircle } from "@tabler/icons-react"
import { Button, InfoBox, Select } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { SETTINGS_SCOPE } from "@tfg/types/constants/communication-studio.constant"
import {
  BulletedListContentSchema,
  COMPONENT_TYPES,
  ComponentContent,
  TextContentJSON,
  TextContentJSONSchema,
} from "@tfg/types/schemas/communication-studio/components"
import { BulletedListContent } from "@tfg/types/types/communication-studio/components"
import {
  COMPONENT_REGISTRY,
  ComponentTypeKey,
  ComponentTypesEnum,
} from "@tfg/types/types/communication-studio/components.types"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { queryClient } from "~/backend/queryClient"
import {
  getProductPageComponentsQueryKey,
  useCreateProductPageComponent,
  useUpdateProductPageComponentsContent,
} from "~/gen/api/hooks"
import {
  useGetComponentRenderer,
  validateWithSchema,
  ComponentValidationResults,
} from "~/pages/Brand/CommunicationStudio/Content/config/component-renderer"
import { ComponentTypeRenderer } from "~/pages/Brand/CommunicationStudio/Languages/components/ComponentTypeRenderer"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"
import { useOrganizationSubscriptionPlanPermissions } from "~/pages/Brand/CommunicationStudio/contexts/organizationSubscriptionPlanPermissions/useOrganizationSubscriptionPlanPermissions"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const ComponentModal = () => {
  const {
    modalState,
    closeModal,
    productPageComponents,
    productSheetGroupId,
    language,
    updateModalContent,
    updateModalConfigurations,
    updateModalComponentType,
  } = useComponentCreation()

  const { permissions } = useOrganizationSubscriptionPlanPermissions()
  const { t } = useTranslation("communicationStudio")

  const error = useMemo(() => {
    if (!permissions) {
      return t("tabs.content.componentModal.error.permissions")
    }
    return null
  }, [permissions, t])

  const isEditMode = modalState.mode === "edit"
  const componentType = modalState.componentType
  const textContent = modalState.textContent
  const configurations = modalState.configurations

  // Wrap update functions to match Dispatch<SetStateAction<...>> signature
  const handleSetTextContent = (
    value:
      | ComponentContent
      | null
      | ((prev: ComponentContent | null) => ComponentContent | null)
  ) => {
    if (typeof value === "function") {
      const newValue = value(textContent)
      if (newValue !== null) {
        updateModalContent(newValue)
      }
    } else if (value !== null) {
      updateModalContent(value)
    }
  }

  const handleSetConfigurations = (
    value:
      | Partial<ContentFilteringProps>
      | null
      | ((
          prev: Partial<ContentFilteringProps> | null
        ) => Partial<ContentFilteringProps> | null)
  ) => {
    updateModalConfigurations(
      typeof value === "function" ? value(configurations) : value
    )
  }

  const modalTitle = componentType
    ? t(`tabs.content.components.${componentType}`)
    : t("tabs.content.componentModal.create.title")

  const { mutate: createComponent, isPending: isCreatingComponent } =
    useCreateProductPageComponent({
      mutation: {
        onSuccess: () => {
          showSuccessNotification({
            title: t("tabs.content.componentModal.create.success.title"),
            message: t("tabs.content.componentModal.create.success.message", {
              componentType: componentType
                ? t(
                    `tabs.content.components.${componentType as ComponentTypeKey}`
                  )
                : "component",
            }),
          })
          queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(
              productSheetGroupId ?? ""
            ),
          })
          closeModal()
        },
        onError: (error) => {
          showAxiosError({
            showNotification: true,
            notificationMessage: t(
              "tabs.content.componentModal.create.error.message",
              {
                componentType: t(
                  `tabs.content.components.${COMPONENT_TYPES.parse(componentType)}`
                ),
              }
            ),
            notificationTitle: t(
              "tabs.content.componentModal.create.error.title"
            ),
            error,
          })
        },
      },
    })

  const { mutate: updateComponent, isPending: isUpdatingComponent } =
    useUpdateProductPageComponentsContent({
      mutation: {
        onSuccess: () => {
          showSuccessNotification({
            title: t("tabs.content.componentModal.update.success.title"),
            message: t("tabs.content.componentModal.update.success.message", {
              componentType: componentType
                ? t(`tabs.content.components.${componentType}`)
                : "component",
            }),
          })
          queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(
              productSheetGroupId ?? ""
            ),
          })
          closeModal()
        },
        onError: (error) => {
          showAxiosError({
            showNotification: true,
            notificationMessage: t(
              "tabs.content.componentModal.update.error.message",
              {
                componentType: t(
                  `tabs.content.components.${COMPONENT_TYPES.parse(componentType)}`
                ),
              }
            ),
            notificationTitle: t(
              "tabs.content.componentModal.update.error.title"
            ),
            error,
          })
        },
      },
    })

  const updateTextContent = (
    blockIndex: number,
    textIndex: number,
    newValue: string
  ) => {
    if (!textContent?.[language]) return

    const updatedBlocks = [...textContent[language]]
    const blockContent = updatedBlocks[blockIndex].textContent
    let updatedTextContent: TextContentJSON[] | BulletedListContent | undefined
    const blockContentParsed = z
      .array(TextContentJSONSchema)
      .safeParse(blockContent)
    if (blockContentParsed.success) {
      updatedTextContent = [...blockContentParsed.data]
      updatedTextContent[textIndex] = {
        ...updatedTextContent[textIndex],
        text: newValue,
      }
    } else {
      const bulletedListContentParsed =
        BulletedListContentSchema.safeParse(blockContent)
      if (bulletedListContentParsed.success) {
        const updatedItems = [...(bulletedListContentParsed.data.items ?? [])]
        if (updatedItems[textIndex]) {
          updatedItems[textIndex] = {
            ...updatedItems[textIndex],
            text: newValue,
          }
        }
        updatedTextContent = {
          ...bulletedListContentParsed.data,
          items: updatedItems,
        }
      }
    }

    if (!updatedTextContent) return

    updatedBlocks[blockIndex] = {
      ...updatedBlocks[blockIndex],
      textContent: updatedTextContent,
    }

    updateModalContent({
      ...textContent,
      [language]: updatedBlocks,
    })
  }

  const productPageComponentPermissions =
    permissions?.[SETTINGS_SCOPE.PRODUCT_PAGE_COMPONENTS]

  const handleSubmit = () => {
    if (!componentType) return

    if (isEditMode && modalState.componentId) {
      updateComponent({
        id: modalState.componentId,
        data: {
          content: textContent ?? {},
          configurations: configurations ?? null,
        },
      })
    } else {
      createComponent({
        data: {
          productSheetGroupId,
          componentType,
          content: textContent ?? {},
          order: productPageComponents.length + 1,
          configurations: configurations ?? null,
        },
      })
    }
  }

  const handleClose = () => {
    closeModal()
  }

  const isPending = isCreatingComponent || isUpdatingComponent
  const componentMetadata = useGetComponentRenderer(componentType)

  const validations = useMemo<ComponentValidationResults | null>(() => {
    if (!modalState.componentType || !componentMetadata) return null

    const contentValidation = validateWithSchema(
      textContent,
      componentMetadata.contentSchema
    )
    const configurationsValidation = validateWithSchema<
      Partial<ContentFilteringProps>
    >(configurations, componentMetadata.configurationsSchema)

    return {
      content: contentValidation,
      configurations: configurationsValidation,
    }
  }, [modalState.componentType, componentMetadata, textContent, configurations])

  const isSubmitDisabled = useMemo(() => {
    const contentRequired = componentMetadata?.contentSchema !== undefined
    const configurationsRequired =
      componentMetadata?.configurationsSchema !== undefined
    if (!contentRequired && !configurationsRequired) return false

    if (!modalState.componentType || !componentMetadata || !validations)
      return true

    if (contentRequired && !validations.content?.success) {
      return true
    }

    if (configurationsRequired && !validations.configurations?.success) {
      return true
    }

    return false
  }, [componentMetadata, validations, modalState.componentType])

  const componentOptions = Object.values(Object.values(ComponentTypesEnum))
    .filter((type) => type !== ComponentTypesEnum.TEXT_PARAGRAPH)
    .map((type) => {
      const existingCount = productPageComponents.filter(
        (component) => component.componentType === type
      ).length

      const metadata = COMPONENT_REGISTRY[type]
      const maxInstances = metadata?.maxInstances

      const isLocked =
        !productPageComponentPermissions?.components.includes(type)

      const isDisabled =
        maxInstances !== undefined && existingCount >= maxInstances

      return {
        label: t(`tabs.content.components.${type}`),
        value: type,
        disabled: isDisabled,
        locked: isLocked,
        lockedTooltip: isLocked
          ? t("tabs.content.componentModal.componentTypeLockedTooltip")
          : undefined,
        disabledTooltip: isDisabled
          ? t("tabs.content.componentModal.componentTypeDisabledTooltip", {
              maxInstances,
            })
          : undefined,
      }
    })
  return (
    <Modal
      opened={modalState.isOpen}
      onClose={handleClose}
      size="md"
      title={modalTitle}
    >
      {!error ? (
        <Stack>
          {isEditMode ? (
            <Select
              data={componentOptions}
              value={componentType}
              disabled
              label={t("tabs.content.componentModal.componentTypeLabel")}
            />
          ) : (
            <Select
              data={componentOptions}
              value={modalState.componentType}
              onChange={(value) => {
                const componentType = COMPONENT_TYPES.safeParse(value)
                if (componentType && componentType.success) {
                  updateModalComponentType(componentType.data)
                }
              }}
              placeholder={t("tabs.content.componentModal.selectorPlaceholder")}
              searchable
              label={t("tabs.content.componentModal.componentTypeLabel")}
            />
          )}
          {componentType ? (
            <ComponentTypeRenderer
              componentType={componentType}
              setTextContent={handleSetTextContent}
              textContent={textContent}
              updateTextContent={updateTextContent}
              configurations={configurations}
              setConfigurations={handleSetConfigurations}
              validations={validations}
            />
          ) : null}
          <Button
            onClick={handleSubmit}
            loading={isPending}
            disabled={isSubmitDisabled}
          >
            {isEditMode
              ? t("tabs.content.componentModal.update.button")
              : t("tabs.content.componentModal.createButton")}
          </Button>
        </Stack>
      ) : (
        <InfoBox
          variant="error"
          icon={<IconAlertCircle />}
          title="Error finding permissions"
          message={error}
        />
      )}
    </Modal>
  )
}

export const useGetComponentTypeLabel = () => {
  const { t } = useTranslation("communicationStudio")
  return (componentType: ComponentTypeKey) => {
    return t(`tabs.content.components.${componentType}`)
  }
}
